import {Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {ChartQuantity, CUSTOM_DATE_FORMATS} from '../economics-aggregated/ec-economics-aggregated.component';
import {I18NService} from '../../../i18n/i18n.service';
import {combineLatest, forkJoin, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {BillingPeriod, BillingPeriodConfig} from '../billing-filter/ec-billing-filter.component';
import {ECO_ERROR_PARAM_KEY, EconomicsErrorType} from 'smag-commons/dto/eco/errors';
import {
    CACV_STRING,
    ChartOptions,
    DataSourceInfo,
    DEFAULT_DIALOG_MAX_WIDTH,
    DEFAULT_DIALOG_MIN_WIDTH,
    disableDataLabels,
    disableLegend,
    generatesTip,
    getAlgoDialogWidth,
    getDataSourceIconInfo,
    getGridColor,
    getSelectionLineColor,
    getTimelyData,
    getTooltipBackgroundColor,
    getTooltipBorderColor,
    isEnergyCommunity,
    isSingleMonthSelected,
    keepYourSecrets,
    mkAbsorptionParticipationChart,
    mkBillingPeriodParams,
    mkPersonalSharingChart,
    noData,
    sanityFromTimeActivationDate,
    sanityToTime,
    showNullData
} from '../../../shared/ec-economics';
import {isDefined} from 'src/app/commons/commons';
import {distinctUntilChanged, filter, first, map, shareReplay, switchMap, takeUntil} from 'rxjs/operators';
import {ConfigurationService} from '../../../shared/configuration.service';
import {Router} from '@angular/router';
import {can} from '../../../shared/permissions';
import {Permission} from 'smag-commons/dto/api';
import {SmagApiService} from '../../../api/smag-api.service';
import * as moment from 'moment-timezone';
import {hasError, showError} from 'src/app/shared/economics-errors';
import {ECMember, EnergyCommunity, PlantIncentiveMode, THOUSANDTHS} from 'smag-commons/cfg/hierarchy';
import {mkMode} from '../../../shared/energy-community';
import {environment} from '../../../../environments/environment';
import {ApexTitleSubtitle} from 'ng-apexcharts';
import {NiceValuePipe} from '../../../shared/shared.pipe';
import {APP_CONFIG_TOKEN, AppConfig} from '../../../app.config';
import {TIME_DELTA_DAY, TIME_DELTA_HOUR} from '../../../shared/line-chart/line-chart.pipe';
import {SharedConfig} from '../../../shared/shared.config';
import {ResizeService} from '../../../shared/resize.service';
import {MatDialog} from '@angular/material/dialog';
import {CAP as CAP_KEY} from 'smag-commons/cfg/hierarchy/ec';
import {COSTS, EC_PLANTS_REI_REDISTRIBUTION, PLANT_TIP_QUOTE} from 'smag-commons/cfg/economics-values';
import {
    ALGORITHM_CAPPED_VAI, ALGORITHM_CONDO_NEW, ALGORITHM_CONDO_OLD,
    ALGORITHM_FANO,
    ALGORITHM_THOUSANDTH,
    ALGORITHM_VAI,
    ECEconomicsAdvancedUserPod,
    ECEconomicsAdvancedUserPodTimelyData,
    ECEconomicsAdvancedUserPodType,
    ECEconomicsAdvancedUserSummary,
    ECEconomicsCappedVAIRedistributedPodIncentivationAlgorithm,
    ECEconomicsFanoRedistributedPodIncentivationAlgorithm,
    ECEconomicsVAIRedistributedPodIncentivationAlgorithm, STRATEGY_NEW_CONDO, STRATEGY_OLD_CONDO
} from 'smag-commons/dto/eco/community';
import {ECCacvRedistributionMode, ECPlantTipMode, ECPlantTipType} from 'smag-commons/dto/eco';
import {
    getCacv,
    getDedicatedShare,
    getPlantCacv,
    getTip,
    isPlantActive,
    isPodActive,
    NamedECEconomicsAdvancedUserPod,
    showCacv,
    showDedicatedShare
} from '../../../shared/ec-members';


@Component({
    selector: 'app-economics-member-pods',
    templateUrl: './economics-member-pods.component.html',
    styleUrls: ['./economics-member-pods.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({height: '0px', minHeight: '0'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        {provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS}
    ]
})
export class EconomicsMemberPodsComponent implements OnInit, OnDestroy {
    private readonly nvp: NiceValuePipe;
    private destroyed = new Subject<void>();
    private timeZone: string = null;
    private isSingleMonth = false;
    private futureEc = false;
    private communityNotConfigured = false;
    private billingPeriodSubj: ReplaySubject<BillingPeriod> = new ReplaySubject<BillingPeriod>(1);
    private cacvMode: ECCacvRedistributionMode = null;

    @ViewChild('ecWaitingForApproval') private ecWaitingForApprovalDialog: TemplateRef<any>;
    @ViewChild('incentiveCalculation') private incentiveCalculationDialog: TemplateRef<any>;

    hierarchyEntity: Observable<HierarchyEntity> = null;
    billingPeriodCfg: BillingPeriodConfig = null;
    billingPeriod: BillingPeriod = {} as BillingPeriod;
    pods: NamedECEconomicsAdvancedUserPod[] = null;
    currentPod: NamedECEconomicsAdvancedUserPod = null;
    displayedColumns: string[] = ['chevron', 'name', 'podType', 'pod', 'tip'];
    dataSourceInfo: DataSourceInfo;
    sharedConfig: SharedConfig;
    contentResize: Observable<void>;
    inactiveEc = false;
    donutChart: {} = null;
    producerChart: ChartQuantity[] = null;
    prosumerChart: ChartQuantity[] = null;
    consumerChart: ChartQuantity[] = null;
    response: ECEconomicsAdvancedUserSummary = null;
    sharingChart: Partial<ChartOptions>;
    participationChart: Partial<ChartOptions>;
    fanoParticipationChart: Partial<ChartOptions>;

    readonly EconomicsErrorType = EconomicsErrorType;
    readonly isDefined = isDefined;
    readonly hasError = hasError;
    readonly showError = showError;
    readonly getSelectionLineColor = getSelectionLineColor;
    readonly getTooltipBorderColor = getTooltipBorderColor;
    readonly getTooltipBackgroundColor = getTooltipBackgroundColor;
    readonly getGridColor = getGridColor;
    readonly ALGORITHM_VAI = ALGORITHM_VAI;
    readonly ALGORITHM_CAPPED_VAI = ALGORITHM_CAPPED_VAI;
    readonly ALGORITHM_THOUSANDTH = ALGORITHM_THOUSANDTH;
    readonly ALGORITHM_FANO = ALGORITHM_FANO;
    readonly THOUSANDTHS = THOUSANDTHS;
    readonly ALGORITHM_CONDO_NEW = ALGORITHM_CONDO_NEW;
    readonly ALGORITHM_CONDO_OLD = ALGORITHM_CONDO_OLD;
    readonly CAP = CAP_KEY;
    readonly ECEconomicsAdvancedUserPodType = ECEconomicsAdvancedUserPodType;
    readonly CACV_STRING = CACV_STRING;
    readonly ECPlantTipType = ECPlantTipType;
    readonly ECPlantTipMode = ECPlantTipMode;
    private strategy: string;


    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly router: Router,
                private readonly hierarchyService: HierarchyService,
                private readonly configurationService: ConfigurationService,
                private readonly apiService: SmagApiService,
                private readonly i18n: I18NService,
                private readonly resizeService: ResizeService,
                private readonly dialog: MatDialog) {
        this.nvp = new NiceValuePipe(i18n);
        this.sharedConfig = cfg.shared;
        this.contentResize = this.resizeService.observe(['content']).pipe(takeUntil(this.destroyed));
    }

    ngOnInit(): void {

        this.configurationService.timeConfig
            .pipe(first())
            .subscribe(timeCfg => this.timeZone = timeCfg.time_zone);

        // as we store time range in URL, we need to avoid "navigation" to same entity when setting URL parameters for time-range
        this.hierarchyEntity = this.hierarchyService.selection.pipe(
            distinctUntilChanged((h1, h2) => h1?.id === h2?.id),
            shareReplay(1),
            takeUntil(this.destroyed)
        );

        this.hierarchyEntity.pipe(
            filter(hierarchyEntity => !!hierarchyEntity),
            switchMap(hierarchyEntity => can(hierarchyEntity.permissions, Permission.READ) ?
                this.apiService.getEntity(hierarchyEntity.id) :
                of(null)),
            takeUntil(this.destroyed)
        ).subscribe(entity => {
            this.billingPeriodCfg = {
                pastDate: entity ? (entity as EnergyCommunity).activation_date || null : null,
                futureDate: null
            };
            if (!isDefined(this.billingPeriodCfg.pastDate)) {
                this.inactiveEc = true;
                this.showWaitingForApprovalModal();
            } else {
                this.inactiveEc = false;
                if (this.billingPeriodCfg.pastDate > moment.tz(this.timeZone).valueOf()) {
                    this.futureEc = true;
                    this.showWaitingForApprovalModal();
                } else {
                    this.futureEc = false;
                }
            }
        });

        combineLatest([
            this.hierarchyEntity.pipe(filter(he => isEnergyCommunity(he))),
            this.billingPeriodSubj,
            this.configurationService.timeConfig
        ]).pipe(switchMap(([he, billingPeriod, timeCfg]) => forkJoin([
                this.apiService.ecEconomicsSummaryAdvanced(he.id, sanityFromTimeActivationDate(billingPeriod.fromTime,
                        this.billingPeriodCfg.pastDate, this.timeZone),
                    sanityToTime(timeCfg.time_zone, billingPeriod.toTime)),
                of(he),
                of(timeCfg.time_zone),
                this.apiService.getSubjectInfo().pipe(map(subjectInfo => subjectInfo.subject)),
                of(billingPeriod)])),
            takeUntil(this.destroyed))
            .subscribe(([response, he, tz, member, billingPeriod]) => {
                this.response = response;
                this.strategy = response.strategy;
                this.billingPeriod = billingPeriod;
                this.isSingleMonth = isSingleMonthSelected(billingPeriod as BillingPeriod, tz, this.showNullData);
                this.communityNotConfigured = (response.errors || [])
                    .filter(err => err.type === EconomicsErrorType.MISSING_PARAM)
                    .some(err =>
                        err.metadata[ECO_ERROR_PARAM_KEY] === COSTS ||
                        err.metadata[ECO_ERROR_PARAM_KEY] === EC_PLANTS_REI_REDISTRIBUTION ||
                        err.metadata[ECO_ERROR_PARAM_KEY] === PLANT_TIP_QUOTE);
                this.mkPods(response.pods.pods, he, member);
                this.cacvMode = response.pods.c_acv_mode;
            });

        this.hierarchyEntity.pipe(
            filter(he => he !== null && can(he.permissions, Permission.READ)),
            switchMap(he => this.apiService.communityPodsStatistics(he.id).pipe(
                map(stats => getDataSourceIconInfo(mkMode(stats))))),
            takeUntil(this.destroyed)
        ).subscribe(dataSourceIconInfo => {
            this.dataSourceInfo = dataSourceIconInfo;
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    showWaitingForApprovalModal() {
        this.dialog.open(this.ecWaitingForApprovalDialog, {
            maxWidth: `${DEFAULT_DIALOG_MAX_WIDTH}px`,
            minWidth: `${DEFAULT_DIALOG_MIN_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe();
    }

    onChangeBillingPeriod(period: BillingPeriod) {
        this.billingPeriodSubj.next(period);
        this.billingPeriod = period;
        this.router.navigate([],
            {
                queryParams: mkBillingPeriodParams(period),
                queryParamsHandling: 'merge'
            }).then();
    }

    onSelectPlant(pod: NamedECEconomicsAdvancedUserPod) {
        if (this.currentPod !== pod && pod.isActive) {
            this.currentPod = pod;
            this.mkCharts(this.currentPod);
        } else if (this.currentPod === pod && pod.isActive) {
            this.currentPod = null;
        }
    }

    onClickIncentiveCalculation(algorithm: string) {
        this.dialog.open(this.incentiveCalculationDialog, {
            data: {algorithm},
            width: `${DEFAULT_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe();
    }

    isDetailViewable(entity: HierarchyEntity): boolean {
        return !!entity && can(entity.permissions, Permission.READ);
    }

    arePlantsActive(pod: NamedECEconomicsAdvancedUserPod) {
        return pod.namedPlants.map(plant => plant.isActive).reduce((acc, val) => acc && val, true);
    }

    get absorptionParticipation() {
        return this.currentPod.absorption_shared_energy * 100 / (this.response.gse ? this.response.shared_energy : this.response.estimated_shared_energy);
    }

    get fanoPodAbsorbedEnergy() {
        return (this.currentPod.absorption_incentivation.algorithm as ECEconomicsFanoRedistributedPodIncentivationAlgorithm).working_hour_pod_absorbed_energy;
    }

    get fanoCommunityAbsorbedEnergy() {
        return (this.currentPod.absorption_incentivation.algorithm as ECEconomicsFanoRedistributedPodIncentivationAlgorithm).working_hour_community_absorbed_energy;
    }

    get fanoAbsorptionParticipation() {
        const algorithm = this.currentPod.absorption_incentivation.algorithm as ECEconomicsFanoRedistributedPodIncentivationAlgorithm;
        return isDefined(algorithm.performance) ? algorithm.performance * 100 : 0;
    }

    get showNullData(): boolean {
        return showNullData(this.inactiveEc, this.futureEc);
    }

    get keepYourSecrets(): boolean {
        return keepYourSecrets(this.inactiveEc, this.futureEc, this.isCommunityNotConfigured());
    }

    private isCommunityNotConfigured() {
        if (this.strategy === STRATEGY_NEW_CONDO || this.strategy === STRATEGY_OLD_CONDO ) {
            return false;
        }
        return this.communityNotConfigured;
    }

    get performanceRevenue(): number {
        if (this.response.redistribution.algorithm !== ALGORITHM_VAI &&
            this.response.redistribution.algorithm !== ALGORITHM_CAPPED_VAI &&
            this.response.redistribution.algorithm !== ALGORITHM_FANO ||
            !this.currentPod.absorption_incentivation) {
            return null;
        }
        return (this.currentPod.absorption_incentivation.algorithm as
            ECEconomicsVAIRedistributedPodIncentivationAlgorithm |
            ECEconomicsCappedVAIRedistributedPodIncentivationAlgorithm |
            ECEconomicsFanoRedistributedPodIncentivationAlgorithm).performance_revenue;
    }

    get fixedRevenue(): number {
        if (this.response.redistribution.algorithm !== ALGORITHM_VAI &&
            this.response.redistribution.algorithm !== ALGORITHM_CAPPED_VAI &&
            !this.currentPod.absorption_incentivation) {
            return null;
        }
        return (this.currentPod.absorption_incentivation.algorithm as
            ECEconomicsVAIRedistributedPodIncentivationAlgorithm |
            ECEconomicsCappedVAIRedistributedPodIncentivationAlgorithm).fixed_revenue;
    }

    get founderPercentage(): number {
        if (this.response.redistribution.algorithm !== ALGORITHM_FANO && !this.currentPod.absorption_incentivation) {
            return null;
        }
        return (this.currentPod.absorption_incentivation.algorithm as ECEconomicsFanoRedistributedPodIncentivationAlgorithm).founder_percentage;
    }

    get timeDelta() {
        return this.isSingleMonth ? TIME_DELTA_HOUR : TIME_DELTA_DAY;
    }

    get nonIncentivizedPlants() {
        return this.currentPod.plants.some(plant => plant.incentive_mode === PlantIncentiveMode.NO_INCENTIVE);
    }

    get personalSharingWidth(): number {
        if (this.response.redistribution.algorithm === ALGORITHM_THOUSANDTH) {
            return 30;
        }
        return this.standardWidth;
    }

    get standardWidth(): number {
        return getAlgoDialogWidth(this.algorithm);
    }

    get algorithm() {
        return this.response.redistribution.algorithm;
    }

    get showDedicatedShare() {
        return !this.keepYourSecrets && showDedicatedShare(this.currentPod);
    }

    get showCacv() {
        return showCacv(this.currentPod);
    }

    get tip() {
        return getTip(this.currentPod, this.nvp, this.i18n);
    }

    get cacv() {
        return getCacv(this.currentPod, this.cacvMode, this.i18n);
    }

    get plantCacv() {
        return getPlantCacv(this.currentPod, this.cacvMode, this.nvp);
    }

    get dedicatedShare() {
        return getDedicatedShare(this.currentPod, this.nvp);
    }

    private mkCharts(pod: NamedECEconomicsAdvancedUserPod) {
        this.donutChart = {
            chart: {
                type: 'donut',
                height: 100,
                offsetX: -100
            },
            colors: [environment.appColors.effectiveInjectedEnergy, environment.appColors.ecoEmpty],
            dataLabels: disableDataLabels(),
            apexLegends: disableLegend(),
            plotOptions: {
                pie: {
                    donut: {
                        size: '65%'
                    }
                }
            },
            series: pod.injection_share_score ?
                [pod.injection_share_score * 100, (1 - pod.injection_share_score) * 100] : [0, 100],
            tooltip: {enabled: false},
            title: this.mkChartTitle(this.nvp.transform(pod.injection_share_score ? pod.injection_share_score * 100 : 0, '%', 1),
                environment.appColors.ecoDarkLegend)
        };

        if (this.response.redistribution.algorithm === ALGORITHM_THOUSANDTH ||
            this.response.redistribution.algorithm === ALGORITHM_VAI ||
            this.response.redistribution.algorithm === ALGORITHM_CAPPED_VAI ||
            this.response.redistribution.algorithm === ALGORITHM_FANO) {
            const personalSharing = isDefined(this.currentPod.absorption_share_score) ? this.currentPod.absorption_share_score * 100 : null;
            this.sharingChart = mkPersonalSharingChart(this.nvp, this.i18n, personalSharing);
        }
        if (this.response.redistribution.algorithm === ALGORITHM_VAI ||
            this.response.redistribution.algorithm === ALGORITHM_CAPPED_VAI) {
            this.participationChart = mkAbsorptionParticipationChart(this.nvp, this.i18n, this.absorptionParticipation);
        }
        if (this.response.redistribution.algorithm === ALGORITHM_FANO) {
            this.fanoParticipationChart = mkAbsorptionParticipationChart(this.nvp, this.i18n, this.fanoAbsorptionParticipation, 'Participation in community');
        }

        if (this.currentPod.type === ECEconomicsAdvancedUserPodType.PRODUCER) {
            this.producerChart = this.mkProducerChart(getTimelyData(this.currentPod));
        } else if (this.currentPod.type === ECEconomicsAdvancedUserPodType.PROSUMER) {
            this.prosumerChart = this.mkProsumerChart(getTimelyData(this.currentPod));
        } else if (this.currentPod.type === ECEconomicsAdvancedUserPodType.CONSUMER) {
            this.consumerChart = this.mkConsumerChart(getTimelyData(this.currentPod));
        }
    }

    private mkChartTitle(text: string, color: string): ApexTitleSubtitle {
        return {
            offsetX: 200,
            offsetY: 20,
            floating: true,
            style: {
                fontSize: '28px',
                fontWeight: '700',
                fontFamily: environment.fontFamily,
                color
            },
            text
        };
    }

    private mkProducerChart(timelyData: ECEconomicsAdvancedUserPodTimelyData): ChartQuantity[] {
        return timelyData ? this.cleanChartQuantities([
            {
                name: this.i18n.translate('Sharing from injection'),
                data: [
                    {
                        data: timelyData.times.map((t, i) => [t, timelyData.effective_injected_energy[i]]),
                        style: {color: this.cfg.shared.colors.injectedPower, strokeWidth: 3},
                        name: this.i18n.translate('Effective injected energy')
                    },
                    {
                        data: timelyData.times.map((t, i) => [t, timelyData.injection_shared_energy[i]]),
                        style: {color: this.cfg.shared.colors.effectiveInjectedEnergy, strokeWidth: 3},
                        name: this.i18n.translate('Sharing from injection')
                    }
                ],
                uom: 'kWh'
            }]) : [];
    }

    private mkProsumerChart(timelyData: ECEconomicsAdvancedUserPodTimelyData): ChartQuantity[] {
        return timelyData ? this.cleanChartQuantities([
            {
                name: this.i18n.translate('Sharing performance'),
                data: [
                    {
                        data: timelyData.times.map((t, i) => [t, -timelyData.absorbed_energy[i]]),
                        style: {color: this.cfg.shared.colors.absorbedPower, strokeWidth: 3},
                        name: this.i18n.translate('Absorbed energy')
                    },
                    {
                        data: timelyData.times.map((t, i) => [t, timelyData.effective_injected_energy[i]]),
                        style: {color: this.cfg.shared.colors.injectedPower, strokeWidth: 3},
                        name: this.i18n.translate('Effective injected energy')
                    },
                    {
                        data: timelyData.times.map((t, i) => [t, -timelyData.absorption_shared_energy[i]]),
                        style: {color: this.cfg.shared.colors.sharedPower, strokeWidth: 3},
                        name: this.i18n.translate('Sharing from absorption')
                    },
                    {
                        data: timelyData.times.map((t, i) => [t, timelyData.injection_shared_energy[i]]),
                        style: {color: this.cfg.shared.colors.effectiveInjectedEnergy, strokeWidth: 3},
                        name: this.i18n.translate('Sharing from injection')
                    }
                ],
                uom: 'kWh'
            }]) : [];
    }

    private mkConsumerChart(timelyData: ECEconomicsAdvancedUserPodTimelyData): ChartQuantity[] {
        return timelyData ? this.cleanChartQuantities([
            {
                name: this.i18n.translate('Sharing from absorption'),
                data: [
                    {
                        data: timelyData.times.map((t, i) => [t, timelyData.absorbed_energy[i]]),
                        style: {color: this.cfg.shared.colors.absorbedPower, strokeWidth: 3},
                        name: this.i18n.translate('Absorbed energy')
                    },
                    {
                        data: timelyData.times.map((t, i) => [t, timelyData.absorption_shared_energy[i]]),
                        style: {color: this.cfg.shared.colors.sharedPower, strokeWidth: 3},
                        name: this.i18n.translate('Sharing from absorption')
                    }
                ],
                uom: 'kWh'
            }]) : [];
    }

    public isAlgorithmDisabled(algorithm: string) {
        return algorithm !== ALGORITHM_VAI &&
            algorithm !== ALGORITHM_CAPPED_VAI &&
            algorithm !== ALGORITHM_THOUSANDTH &&
            algorithm !== ALGORITHM_FANO &&
            algorithm !== ALGORITHM_CONDO_NEW &&
            algorithm !== ALGORITHM_CONDO_OLD;
    }

    get showEconomicsPlantsData(){
        return !(this.strategy === STRATEGY_NEW_CONDO || this.strategy === STRATEGY_OLD_CONDO);
    }

    private cleanChartQuantities(chartQuantities: ChartQuantity[]) {
        return chartQuantities.map(qt => ({...qt, data: noData(qt.data) ? [] : qt.data}));
    }

    private mkPods(podsIn: ECEconomicsAdvancedUserPod[], he: HierarchyEntity, member: ECMember) {
        forkJoin(podsIn.map(podIn => this.getNamedPod(podIn, he, member)))
            .subscribe(podsOut => this.pods = podsOut);
    }

    private getNamedPod(pod: ECEconomicsAdvancedUserPod, he: HierarchyEntity, member: ECMember): Observable<NamedECEconomicsAdvancedUserPod> {
        return this.apiService.findEntityByPOD(pod.pod)
            .pipe(switchMap(resp => this.apiService.getECPod(he.id, resp.id)
                .pipe(map(ecNamedPod => ({
                    ...pod,
                    name: ecNamedPod.name,
                    namedPlants: ecNamedPod.plants.map(plant => ({
                        name: plant.name,
                        isActive: isPlantActive(plant),
                        generatesTip: generatesTip(plant.plant)
                    })),
                    isActive: isPodActive(member.relations, pod.pod),
                    generatesTip: ecNamedPod.plants.length > 0 ? ecNamedPod.plants.some(plant => generatesTip(plant.plant)) : null,
                    capitalGrant: ecNamedPod.plants.some(plant => plant.plant.capital_grant > 0)
                })))));
    }

}

